import { useState } from 'react';
import API_ENDPOINTS from '../../config/api';
import { getScenarioContext } from '../../config/scenarioDescriptions';
import { buildPathChain } from '../utils/pathChain';

/**
 * useSimilarAfterAdd — look for what a just-added node may be the same as.
 *
 * Runs after the node is on the map, never before. The result is held as
 * `findings` ({ nodeId, title, existingNodes }) for a notification to show;
 * a failure is a warning in the console and no notification, because the
 * node is already there and nothing was lost.
 */
export default function useSimilarAfterAdd({ pathways, scenarioId, instanceId, scenarioInfo, language }) {
  const [findings, setFindings] = useState(null);

  const findSimilar = async (nodeId, title, parent, description = '') => {
    try {
      const others = pathways.filter((n) => n.id !== nodeId && n.id !== parent?.id
        && n.type !== 'start' && n.type !== 'placeholder' && !n.isTemporary && n.participantId !== 'system');
      const res = await fetch(API_ENDPOINTS.AI_SUGGEST_NODE_CONTENT(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // The embedding is built from what was written, not only the label.
          title: description && description !== title ? `${title}. ${description}`.slice(0, 600) : title,
          parentNodeId: parent?.id,
          scenarioId,
          instanceId,
          allExistingNodes: others.map((n) => ({
            id: n.id, title: n.title || n.text || '', text: n.text || n.title || '',
            description: (n.description || '').slice(0, 300), type: n.type, depth: n.depth,
          })),
          scenarioContext: getScenarioContext(scenarioInfo?.title, parent?.title || parent?.text, language),
          pathChain: buildPathChain(parent, pathways, scenarioInfo, language),
          aiheenRajaus: null,
        }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const similar = (data.existingNodes || []).filter((n) => n.id !== nodeId);
      if (similar.length) setFindings({ nodeId, title, existingNodes: similar });
    } catch (err) {
      console.warn('[impact-map] similarity search failed:', err.message);
    }
  };

  return { findings, findSimilar, clearFindings: () => setFindings(null) };
}
